// Industry presets: a curated bundle of module defaults + vocabulary chosen
// when a company is created ("select industry, then it asks what to include").
// Modules map to entitlement feature keys — features fail OPEN, so existing
// tenants without flags keep seeing everything; a preset writes explicit
// true/false flags for the modules the wizard showed.

export const INDUSTRIES = [
  "garage_door",
  "hvac",
  "plumbing",
  "electrical",
  "landscape",
  "cleaning",
  "handyman",
  "general",
] as const;
export type Industry = (typeof INDUSTRIES)[number];

export interface IndustryModule {
  /** Entitlement feature key written to tenant.featureFlags. */
  key: string;
  label: string;
  hint: string;
  /** Pre-checked in the wizard for this industry. */
  on: boolean;
}

export interface IndustryVocab {
  /** One field worker: "Tech", "Crew member", "Cleaner"… */
  tech: string;
  techs: string;
  job: string;
  jobs: string;
}

export interface IndustryPreset {
  label: string;
  vocab: IndustryVocab;
  modules: IndustryModule[];
  /** Category suggestions for the pricebook (free-string field on items). */
  pricebookCategories: string[];
  /** Named on the roadmap for this vertical; shown, not toggleable. */
  planned: string[];
}

const VOCAB_DEFAULT: IndustryVocab = { tech: "Tech", techs: "Techs", job: "Job", jobs: "Jobs" };

function modules(overrides: Partial<Record<string, boolean>> = {}): IndustryModule[] {
  const base: IndustryModule[] = [
    { key: "workforce", label: "Time clock & timesheets", hint: "GPS punches, approvals, payroll prep", on: true },
    { key: "shiftMarketplace", label: "Shifts & availability", hint: "Open-shift pickup, off-shift hand-raise, jobs near me", on: true },
    { key: "routing", label: "Routing & live map", hint: "Self-optimizing tech routes, ETAs, on-time flags", on: true },
    { key: "inventory", label: "Inventory & parts", hint: "Stock, ordered-parts holds, AI receiving", on: true },
    { key: "requests", label: "Online request intake", hint: "Public request form feeding estimates and jobs", on: true },
    { key: "templates", label: "Templates & agreements", hint: "Service agreements signed on-site or by email", on: true },
  ];
  return base.map((m) => (overrides[m.key] === undefined ? m : { ...m, on: overrides[m.key]! }));
}

export const INDUSTRY_PRESETS: Record<Industry, IndustryPreset> = {
  garage_door: {
    label: "Garage Door",
    vocab: VOCAB_DEFAULT,
    modules: modules(),
    pricebookCategories: ["Springs", "Openers", "Doors", "Panels", "Service & Tune-up", "Remotes & Keypads"],
    planned: [],
  },
  hvac: {
    label: "HVAC",
    vocab: VOCAB_DEFAULT,
    modules: modules(),
    pricebookCategories: ["Maintenance", "Repair", "Install", "IAQ", "Filters & Parts", "Memberships"],
    planned: ["Maintenance memberships"],
  },
  plumbing: {
    label: "Plumbing",
    vocab: VOCAB_DEFAULT,
    modules: modules(),
    pricebookCategories: ["Drain & Sewer", "Water Heaters", "Fixtures", "Repipe", "Service Call"],
    planned: [],
  },
  electrical: {
    label: "Electrical",
    vocab: VOCAB_DEFAULT,
    modules: modules(),
    pricebookCategories: ["Panels", "Lighting", "EV Chargers", "Generators", "Troubleshooting"],
    planned: [],
  },
  landscape: {
    label: "Landscape",
    vocab: { tech: "Crew member", techs: "Crew", job: "Work ticket", jobs: "Work tickets" },
    modules: modules({ inventory: false }),
    pricebookCategories: ["Maintenance", "Enhancements", "Irrigation", "Fertilization", "Cleanups"],
    planned: ["Crew scheduling", "Seasonal contracts", "Production-rate estimating", "Snow & ice mode"],
  },
  cleaning: {
    label: "Cleaning",
    vocab: { tech: "Cleaner", techs: "Cleaners", job: "Job", jobs: "Jobs" },
    modules: modules({ inventory: false }),
    pricebookCategories: ["Recurring Clean", "Deep Clean", "Move In/Out", "Add-ons"],
    planned: ["Recurring visit plans"],
  },
  handyman: {
    label: "Handyman",
    vocab: VOCAB_DEFAULT,
    modules: modules({ shiftMarketplace: false }),
    pricebookCategories: ["Hourly", "Assembly", "Repairs", "Punch List"],
    planned: [],
  },
  general: {
    label: "General field service",
    vocab: VOCAB_DEFAULT,
    modules: modules(),
    pricebookCategories: ["Service", "Install", "Repair", "Materials"],
    planned: [],
  },
};

export function industryPreset(industry: string | null | undefined): IndustryPreset {
  return INDUSTRY_PRESETS[(industry as Industry) ?? "general"] ?? INDUSTRY_PRESETS.general;
}
